import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { ADMIN, AUTOCOMPLETE } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

const UpdateMultipleModuleModal = NiceModal.create(({ ids, modalName }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					moduleIDs: yup.array().nullable()
				})
				.required(),
		[]
	);

	const dispatch = useDispatch();
	const { showError, showSuccess } = useAlert();
	const axiosPrivate = useAxiosPrivate();
	const { closeModal } = useModalRouter();

	const handleCloseModal = () => {
		closeModal();
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			moduleIDs: null
		}
	});

	const {
		setValue,
		watch,
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting }
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		try {
			axiosPrivate.post(ADMIN.ROLE.UPDATE_ROLES, {
				IDs: ids.map((id) => Number(id)),
				moduleIDs: data.moduleIDs?.join(',') ?? null
			});
			showSuccess();
			dispatch(updateDynamicListData(DYNAMICLISTS.ACCESS_CONTROL));
			handleCloseModal();
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const roleAutocompleteColumns = useMemo(
		() => [
			{ accessor: 'ID', width: 100 },
			{ accessor: 'name', width: 1000 }
		],
		[]
	);

	const defaultColSpan = {
		md: 6
	};

	return (
		<Modal maxWidth="small" name={modalName} fullwidth className="modal-updateMultipleModule">
			<ModalTitle>
				<Typography variant="p1">updateMultipleModule</Typography>
			</ModalTitle>

			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="ids" required>
						<Typography>{ids.join(',')}</Typography>
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="moduleIDs" required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.ROLES}
							value={watch('moduleIDs')}
							setValue={(value) => {
								setValue('moduleIDs', value?.map((item) => item.ID) ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							name="moduleIDs"
							columns={roleAutocompleteColumns}
							selectionLabelAccessor={['name']}
							multipleSelection
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button variant="outline" label="cancel" onClick={handleCloseModal} />
				<Button
					label="update"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default UpdateMultipleModuleModal;
